import React, { useEffect, useMemo, useState } from 'react'
import { CButton, CButtonGroup, CFormInput, CInputGroup, CInputGroupText } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import AgencyCard from './AgencyCard'
import AgencyDetailPanel from './AgencyDetailPanel'
import AgencyInformationPage from './AgencyInformationPage'
import AgencyModal from './AgencyModal'
import PlanModal from './PlanModal'
import MonitorModal from '../monitor/MonitorModal'
import Empty from '../../shared/Empty'
import Pager from '../../shared/Pager'
import SkeletonList from '../../shared/SkeletonList'
import { icon } from '../../shared/icons'
import { useUI } from '../../shared/ui/UIProvider'
import { request } from '../../services'

const perPage = 10

function AgenciesPage({ onError }) {
  const { toast } = useUI()
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [planOpen, setPlanOpen] = useState(false)
  const [agencyOpen, setAgencyOpen] = useState(false)
  const [monitorOpen, setMonitorOpen] = useState(false)
  const [openDetails, setOpenDetails] = useState({})
  const [selectedAgency, setSelectedAgency] = useState(null)
  const [lastPlan, setLastPlan] = useState('')

  async function load() {
    setLoading(true)
    const res = await request('list')
    setLoading(false)
    if (!res.ok) {
      const detail = res.data?.error || 'Error al listar agencias'
      onError(detail)
      toast(detail, 'danger')
      return
    }
    setItems(Array.isArray(res.data) ? res.data : [])
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((agency) => [agency.name, agency.parent_pcc, agency.description].join(' ').toLowerCase().includes(term))
  }, [items, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage))
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage)

  if (selectedAgency) {
    return (
      <AgencyInformationPage
        agency={selectedAgency}
        onBack={() => setSelectedAgency(null)}
      />
    )
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Agencias</h1>
          <p>Administra agencias, PCCs, usuarios y acceso a scripts.</p>
        </div>
        <CButtonGroup>
          <CButton color="light" onClick={() => setMonitorOpen(true)}><CIcon icon={icon('cilMonitor')} /> Monitor</CButton>
          <CButton color="light" onClick={load}><CIcon icon={icon('cilReload')} /> Recargar</CButton>
          <CButton color="success" className="btn-brand" onClick={() => setPlanOpen(true)}><CIcon icon={icon('cilPlus')} /> Nueva agencia</CButton>
        </CButtonGroup>
      </div>
      <CInputGroup className="search-wrap">
        <CInputGroupText><CIcon icon={icon('cilMagnifyingGlass')} /></CInputGroupText>
        <CFormInput placeholder="Buscar por nombre, PCC o descripcion" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
      </CInputGroup>
      {loading ? <SkeletonList /> : pageItems.map((agency) => {
        const key = agency.parent_pcc || agency.uuid_payment_plan
        const detailsOpen = Boolean(openDetails[key])
        return (
          <div key={key}>
            <AgencyCard
              agency={agency}
              detailsOpen={detailsOpen}
              onToggleDetails={() => setOpenDetails((current) => ({ ...current, [key]: !current[key] }))}
              onOpenNewDetails={() => setSelectedAgency(agency)}
            />
            <AgencyDetailPanel agency={agency} open={detailsOpen} onError={onError} />
          </div>
        )
      })}
      {!loading && pageItems.length === 0 ? <Empty icon={icon('cilList')} text="No hay agencias para mostrar." /> : null}
      <Pager page={page} total={totalPages} onPage={setPage} />
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} onCreated={(uuid) => { setLastPlan(uuid); setPlanOpen(false); setAgencyOpen(true) }} />
      <AgencyModal open={agencyOpen} planUuid={lastPlan} onClose={() => setAgencyOpen(false)} onCreated={() => { setAgencyOpen(false); toast('Agencia creada correctamente', 'success'); load() }} />
      <MonitorModal open={monitorOpen} onClose={() => setMonitorOpen(false)} />
    </>
  )
}

export default AgenciesPage
